import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { SalePage } from '@/api/saleApi';
import { formatCurrency } from '@/utils/helpers';
import SaleStatusBadge from '@/features/sales/components/SaleStatusBadge';

interface DashboardRecentSalesProps {
  page: SalePage | null;
  scope: 'all' | 'own';
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
}

export const DashboardRecentSales = ({
  page,
  scope,
  isLoading,
  error,
  onRetry,
}: DashboardRecentSalesProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('vi') ? 'vi-VN' : 'en-US';
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <section className="min-w-0 rounded-[20px] border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-950">
            {t('admin.dashboard.recent_sales.title')}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {page
              ? t(`admin.dashboard.recent_sales.description_${scope}`, {
                  total: page.totalElements,
                })
              : t(`admin.dashboard.recent_sales.description_pending_${scope}`)}
          </p>
        </div>
        <Link
          to="/admin/sales/history"
          className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-primary transition-colors hover:text-primary/80"
        >
          {t('admin.dashboard.recent_sales.view_all')}
          <span className="material-symbols-outlined text-lg" aria-hidden="true">
            arrow_forward
          </span>
        </Link>
      </div>

      {isLoading || (!page && !error) ? (
        <div aria-label={t('admin.dashboard.loading')} className="space-y-3 p-5">
          {[0, 1, 2].map((row) => (
            <div key={row} className="h-12 animate-pulse rounded-xl bg-slate-100" />
          ))}
        </div>
      ) : error ? (
        <div className="px-5 py-10 text-center" role="alert">
          <span className="material-symbols-outlined text-4xl text-rose-300">cloud_off</span>
          <h3 className="mt-3 text-sm font-semibold text-slate-900">
            {t('admin.dashboard.recent_sales.error_title')}
          </h3>
          <p className="mt-1 text-sm text-slate-500">{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
          >
            {t('admin.dashboard.actions.retry')}
          </button>
        </div>
      ) : page && page.data.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-3">{t('admin.dashboard.recent_sales.table.code')}</th>
                <th className="px-4 py-3">{t('admin.dashboard.recent_sales.table.customer')}</th>
                <th className="px-4 py-3">{t('admin.dashboard.recent_sales.table.employee')}</th>
                <th className="px-4 py-3 text-right">{t('admin.dashboard.recent_sales.table.total')}</th>
                <th className="px-4 py-3">{t('admin.dashboard.recent_sales.table.status')}</th>
                <th className="px-5 py-3">{t('admin.dashboard.recent_sales.table.time')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {page.data.map((sale) => (
                <tr key={sale.id} className="transition-colors hover:bg-slate-50/70">
                  <td className="whitespace-nowrap px-5 py-4 font-semibold text-slate-900">
                    {sale.code}
                  </td>
                  <td className="px-4 py-4 text-slate-600">{sale.customer?.name || '—'}</td>
                  <td className="px-4 py-4 text-slate-600">{sale.createdBy.username}</td>
                  <td className="whitespace-nowrap px-4 py-4 text-right font-semibold text-slate-900">
                    {formatCurrency(sale.totalAmount)}
                  </td>
                  <td className="px-4 py-4">
                    <SaleStatusBadge status={sale.status} />
                  </td>
                  <td className="whitespace-nowrap px-5 py-4 text-slate-500">
                    {dateFormatter.format(new Date(sale.createdAt))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="px-5 py-12 text-center">
          <span className="material-symbols-outlined text-4xl text-slate-300">receipt_long</span>
          <h3 className="mt-3 text-sm font-semibold text-slate-900">
            {t('admin.dashboard.recent_sales.empty_title')}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {t('admin.dashboard.recent_sales.empty_description')}
          </p>
        </div>
      )}
    </section>
  );
};
